import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type Prefs = {
  user_id: string;
  inapp_publishing: boolean;
  inapp_readwise: boolean;
  inapp_billing: boolean;
  inapp_workspace: boolean;
  inapp_affiliate: boolean;
};

type Toggle = {
  key: keyof Omit<Prefs, "user_id">;
  label: string;
  description: string;
};

const TOGGLES: Toggle[] = [
  {
    key: "inapp_publishing",
    label: "Publishing",
    description: "When publications are dispatched or fail to send.",
  },
  {
    key: "inapp_readwise",
    label: "Readwise sync",
    description: "When daily Readwise sync completes or fails.",
  },
  {
    key: "inapp_billing",
    label: "Billing updates",
    description: "Trial ending, plan changes, and cancellations. Failed payments are always sent.",
  },
  {
    key: "inapp_workspace",
    label: "Workspace activity",
    description: "When invited members accept and join your workspace.",
  },
  {
    key: "inapp_affiliate",
    label: "Affiliate program",
    description: "When pending commissions are approved and become eligible for payout.",
  },
];

const defaults = (userId: string): Prefs => ({
  user_id: userId,
  inapp_publishing: true,
  inapp_readwise: true,
  inapp_billing: true,
  inapp_workspace: true,
  inapp_affiliate: true,
});

export const NotificationPreferencesCard = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [saving, setSaving] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["user_notification_preferences", user?.id ?? "anon"],
    queryFn: async (): Promise<Prefs> => {
      if (!user) return defaults("");
      const { data, error } = await supabase
        .from("user_notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return (data as Prefs | null) ?? defaults(user.id);
    },
    enabled: !!user,
  });

  // Local mirror so toggles feel snappy while the network round-trips.
  const [local, setLocal] = useState<Prefs | null>(null);
  useEffect(() => {
    if (data) setLocal(data);
  }, [data]);

  const handleToggle = async (key: Toggle["key"], next: boolean) => {
    if (!user || !local) return;
    const previous = local;
    const optimistic = { ...local, [key]: next };
    setLocal(optimistic);
    setSaving(key);
    const { error } = await supabase
      .from("user_notification_preferences")
      .upsert({ ...optimistic }, { onConflict: "user_id" });
    setSaving(null);
    if (error) {
      setLocal(previous);
      toast.error("Couldn't save preference", { description: error.message });
      return;
    }
    qc.setQueryData(["user_notification_preferences", user.id], optimistic);
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-4 w-4" /> Notification preferences
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Choose which in-app notifications you want to receive. Critical alerts (failed payments, system messages) are always delivered.
        </p>

        <div className="divide-y rounded-md border">
          {TOGGLES.map((t) => {
            const checked = local ? local[t.key] : true;
            return (
              <div
                key={t.key}
                className="flex items-start justify-between gap-4 px-3 py-3"
              >
                <div className="min-w-0 flex-1 space-y-0.5">
                  <Label htmlFor={`notif-${t.key}`} className="text-sm font-medium">
                    {t.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{t.description}</p>
                </div>
                <Switch
                  id={`notif-${t.key}`}
                  checked={checked}
                  disabled={isLoading || !local || saving === t.key}
                  onCheckedChange={(v) => void handleToggle(t.key, v)}
                />
              </div>
            );
          })}

          <div className="flex items-start justify-between gap-4 px-3 py-3 bg-muted/30">
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">System & critical alerts</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="gap-1 text-[10px]">
                      <Lock className="h-3 w-3" /> Always on
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Failed payments and system announcements are always delivered so you don't miss something that needs your attention.
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground">
                Failed payment alerts and important system notices.
              </p>
            </div>
            <Switch checked disabled />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
